import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Entity } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import Helmet from 'react-helmet';
import { UiComponentName, useComponentProps } from '@shapeable/ui';

const cls = classNames('raisely-donate-widget');

declare global {
  interface Window {
    RaiselyEmbed?: {
      init?: () => void;
    };
  }
}

// -------- Props -------->
export type RaiselyDonateWidgetProps = Classable & HasChildren & { 
  entity?: Entity;
  campaignPath?: string;
  profile?: string;
  width?: string;
  height?: string;
};

export const RaiselyDonateWidgetDefaultProps: Omit<RaiselyDonateWidgetProps, 'entity'> = {
  campaignPath: 'future-council',
  profile: '',
  width: '100%',
  height: '800'
};

// -------- Styles -------->
const ContainerStyles = breakpoints({
  base: css`
    width: 100%;
    background: ${theme.COLOR('#F2EAE2')};
  `,
});

// -------- Components -------->
const My = {
  NAME: 'RaiselyDonateWidget' as UiComponentName,
  Container: styled.div`${ContainerStyles}`,
};

export const RaiselyDonateWidget: Shapeable.FC<RaiselyDonateWidgetProps> = (props) => {
  const { className, children, entity, campaignPath, profile, width, height } = useComponentProps(
    My.NAME, 
    RaiselyDonateWidgetDefaultProps, 
    props
  );

  useEffect(() => {
    const initWidget = () => {
      if (window.RaiselyEmbed && typeof window.RaiselyEmbed.init === 'function') {
        try {
          window.RaiselyEmbed.init();
        } catch (error) {
          console.warn('Raisely widget initialization failed:', error);
        }
      } else {
        // Script not ready yet, try again after a short delay
        setTimeout(initWidget, 100);
      }
    };

    initWidget();
  }, [campaignPath, profile]);

  return (
    <My.Container className={cls.name(className)}>
      <Helmet>
        <script src="https://cdn.raisely.com/v3/public/embed.js" />
      </Helmet>
      <div 
        className="raisely-donate" 
        data-campaign-path={campaignPath}
        data-profile={profile}
        data-width={width}
        data-height={height}
      />
    </My.Container>
  );
};

RaiselyDonateWidget.cls = cls;